import { useEffect } from 'react'
import { Box, Divider, IconButton, MenuItem, Select, Stack, Tooltip } from '@mui/material'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'
import TableChartIcon from '@mui/icons-material/TableChart'
import LinkIcon from '@mui/icons-material/Link'
import ImageIcon from '@mui/icons-material/Image'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Image } from '@tiptap/extension-image'
import { TextAlign } from '@tiptap/extension-text-align'
// TipTap v3: StarterKit now bundles Underline and Link (configured below), and
// the table extensions are named exports of @tiptap/extension-table.
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table'

// Fixed color palette — the class names MUST match the server sanitizer
// allowlist (services/richtext.py) or they'll be stripped on save.
const COLORS = [
  { label: 'Default', cls: '' },
  { label: 'Primary', cls: 'vc-color-primary' },
  { label: 'Accent', cls: 'vc-color-accent' },
  { label: 'Muted', cls: 'vc-color-muted' },
  { label: 'Success', cls: 'vc-color-success' },
  { label: 'Danger', cls: 'vc-color-danger' },
]

/**
 * TipTap WYSIWYG editor. Emits sanitized-friendly HTML (headings, bold, italic,
 * underline, lists, blockquote, links, alignment, tables, images, and a fixed
 * color palette applied as allowlisted classes). The server sanitizes on save.
 */
export default function RichTextEditor({
  valueHtml,
  onChangeHtml,
  onPickImage,
}: {
  valueHtml: string
  onChangeHtml: (html: string) => void
  onPickImage?: () => Promise<string | null>
}) {
  const editor = useEditor({
    extensions: [
      // Underline + Link are part of StarterKit in v3; configure Link through it.
      StarterKit.configure({
        link: { openOnClick: false, autolink: true },
      }),
      Image,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: valueHtml || '',
    // v3 removed the immediate-render default that emitted before mount; keep
    // SSR-safe rendering off (client-only editor).
    immediatelyRender: false,
    onUpdate: ({ editor }) => onChangeHtml(editor.getHTML()),
  })

  useEffect(() => {
    if (editor && valueHtml !== editor.getHTML()) {
      // v3: the second arg is an options object, not a boolean emitUpdate flag.
      editor.commands.setContent(valueHtml || '', { emitUpdate: false })
    }
  }, [valueHtml, editor])

  if (!editor) return null

  const btn = (
    title: string,
    active: boolean,
    onClick: () => void,
    icon: React.ReactNode,
  ) => (
    <Tooltip title={title}>
      <IconButton size="small" onClick={onClick} color={active ? 'primary' : 'default'}>
        {icon}
      </IconButton>
    </Tooltip>
  )

  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Link URL', prev ?? 'https://')
    if (url === null) return
    if (url === '') {
      editor.chain().focus().unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  const addImage = async () => {
    if (!onPickImage) return
    const url = await onPickImage()
    if (url) editor.chain().focus().setImage({ src: url }).run()
  }

  const currentColor =
    COLORS.find((c) => c.cls && editor.isActive('paragraph', { class: c.cls }))?.cls ?? ''

  const applyColor = (cls: string) => {
    // Apply the color class to the current text node's mark via a span-like
    // approach: toggle a class on the selected paragraph. TipTap doesn't ship a
    // class mark by default, so we wrap the selection with an inline <span>.
    const chain = editor.chain().focus()
    // Use link-free span via HTML insertion of the selection.
    const { from, to } = editor.state.selection
    const text = editor.state.doc.textBetween(from, to)
    if (!text) return
    if (cls) {
      chain.insertContent(`<span class="${cls}">${text}</span>`).run()
    } else {
      chain.run()
    }
  }

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Stack
        direction="row"
        spacing={0.25}
        alignItems="center"
        sx={{ p: 0.5, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}
      >
        <Select
          size="small"
          variant="standard"
          disableUnderline
          value={
            editor.isActive('heading', { level: 1 })
              ? 'h1'
              : editor.isActive('heading', { level: 2 })
                ? 'h2'
                : editor.isActive('heading', { level: 3 })
                  ? 'h3'
                  : 'p'
          }
          onChange={(e) => {
            const v = e.target.value
            if (v === 'p') editor.chain().focus().setParagraph().run()
            else
              editor
                .chain()
                .focus()
                .toggleHeading({ level: Number(v.slice(1)) as 1 | 2 | 3 })
                .run()
          }}
          sx={{ minWidth: 90, mx: 0.5 }}
        >
          <MenuItem value="p">Paragraph</MenuItem>
          <MenuItem value="h1">Heading 1</MenuItem>
          <MenuItem value="h2">Heading 2</MenuItem>
          <MenuItem value="h3">Heading 3</MenuItem>
        </Select>
        <Divider orientation="vertical" flexItem />
        {btn('Bold', editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), <FormatBoldIcon fontSize="small" />)}
        {btn('Italic', editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), <FormatItalicIcon fontSize="small" />)}
        {btn('Underline', editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), <FormatUnderlinedIcon fontSize="small" />)}
        {btn('Quote', editor.isActive('blockquote'), () => editor.chain().focus().toggleBlockquote().run(), <FormatQuoteIcon fontSize="small" />)}
        <Divider orientation="vertical" flexItem />
        {btn('Bullet list', editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), <FormatListBulletedIcon fontSize="small" />)}
        {btn('Numbered list', editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), <FormatListNumberedIcon fontSize="small" />)}
        <Divider orientation="vertical" flexItem />
        {btn('Align left', editor.isActive({ textAlign: 'left' }), () => editor.chain().focus().setTextAlign('left').run(), <FormatAlignLeftIcon fontSize="small" />)}
        {btn('Align center', editor.isActive({ textAlign: 'center' }), () => editor.chain().focus().setTextAlign('center').run(), <FormatAlignCenterIcon fontSize="small" />)}
        {btn('Align right', editor.isActive({ textAlign: 'right' }), () => editor.chain().focus().setTextAlign('right').run(), <FormatAlignRightIcon fontSize="small" />)}
        <Divider orientation="vertical" flexItem />
        {btn('Link', editor.isActive('link'), setLink, <LinkIcon fontSize="small" />)}
        {btn('Table', false, () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(), <TableChartIcon fontSize="small" />)}
        {onPickImage && btn('Image', false, addImage, <ImageIcon fontSize="small" />)}
        <Select
          size="small"
          variant="standard"
          disableUnderline
          value={currentColor}
          displayEmpty
          onChange={(e) => applyColor(e.target.value)}
          sx={{ minWidth: 90, mx: 0.5 }}
          renderValue={(v) => COLORS.find((c) => c.cls === v)?.label ?? 'Color'}
        >
          {COLORS.map((c) => (
            <MenuItem key={c.label} value={c.cls}>
              {c.label}
            </MenuItem>
          ))}
        </Select>
      </Stack>
      <Box
        sx={{
          p: 1.5,
          minHeight: 140,
          '& .ProseMirror': { outline: 'none', minHeight: 120 },
          '& .ProseMirror p': { margin: '0 0 8px' },
          '& table': { borderCollapse: 'collapse', width: '100%' },
          '& td,& th': { border: '1px solid #ccc', padding: '4px 8px' },
          '& .vc-color-primary': { color: '#0075BC' },
          '& .vc-color-accent': { color: '#1C5580' },
          '& .vc-color-muted': { color: '#51788F' },
          '& .vc-color-success': { color: '#2e7d32' },
          '& .vc-color-danger': { color: '#d32f2f' },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  )
}
